import { Component, Host, h, Prop, State, Element, Fragment } from '@stencil/core';
import { LANGUAGE_CODE } from '../../../constant/language';
import { toColorRgb } from '../../../utils/colorHelper';
import { prefixZero } from '../../../utils/stringHelper';
import { getStyleObjectFromString } from '../../../utils/cssHelper';

@Component({
  tag: 'cms-countdown-timer',
  styleUrl: 'cms-countdown-timer.scss',
  shadow: false,
})
export class CmsCountdownTimer {
  /**
   * The language code of the component.
   */
  @Prop() dataLanguageCode: string = LANGUAGE_CODE.ENGLISH;

  /**
   * The end time (UTC). YYYY-MM-DDTHH:mm:ss.sssZ or timestamp
   */
  @Prop() dataEndTimeUtc: string = '';

  /**
   * Whether to hide days.
   */
  @Prop() dataIsHideDays: boolean = false;

  /**
   * Whether to show seconds.
   */
  @Prop() dataIsShowSeconds: boolean = false;

  /**
   * The theme of the component.
   */
  @Prop() dataTheme: 'default' | 'bordered' | 'color-bg' | 'gradient-bg' | 'black-white' | 'text-only' | 'capsule' =
    'default';

  /**
   * The size of the component.
   */
  @Prop() dataSize: 'large' | 'middle' | 'small' | 'mini' = 'middle';

  /**
   * The primary color of the component.
   */
  @Prop() dataPrimaryColor: string = '';

  /**
   * Whether to display ends in text.
   */
  @Prop() dataIsShowEndsIn: boolean = true;

  /**
   * Whether the ends in text will wrap.
   */
  @Prop() dataIsEndsInWrap: boolean = false;

  /**
   * Whether the digit is round.
   */
  @Prop() dataIsDigitRound: boolean = false;

  /**
   * Whether the digit and the letter is round.
   */
  @Prop() dataIsDigitAndLetterRound: boolean = false;

  /**
   * Whether to show colons.
   */
  @Prop() dataIsShowColon: boolean = false;

  /**
   * Whether to hide letters.
   */
  @Prop() dataIsHideLetter: boolean = false;

  /**
   * Whether to translate letters.
   */
  @Prop() dataIsTranslateLetter: boolean = true;
  /**
   * Whether to lowercase letters.
   */
  @Prop() dataIsLowercaseLetter: boolean = false;

  /**
   * ends in style
   */
  @Prop() dataEndsInStyle: string = '';

  /**
   * letter style
   */
  @Prop() dataLetterStyle: string = '';

  /**
   * digit style
   */
  @Prop() dataDigitStyle: string = '';

  /**
   * colon style
   */
  @Prop() dataColonStyle: string = '';

  /**
   * behavior after the end
   */
  @Prop() dataBehaviorAfterTheEnd: 'hide' | 'show' = 'hide';

  @State() currentTime: Date = new Date();

  @Element() elRoot: HTMLElement;

  // adhoc translation config
  private TRANSLATION_CONFIG = {
    [LANGUAGE_CODE.ENGLISH]: {
      ENDS_IN_TEXT: 'Ends in',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.SPANISH]: {
      ENDS_IN_TEXT: 'Acaba en',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.JAPANESE]: {
      ENDS_IN_TEXT: '終了まで',
      DAYS_LETTER: '日',
      HOURS_LETTER: '時間',
      MINUTES_LETTER: '分',
      SECONDS_LETTER: '秒',
    },
    [LANGUAGE_CODE.KOREAN]: {
      ENDS_IN_TEXT: '행사 종료',
      DAYS_LETTER: '일',
      HOURS_LETTER: '시간',
      MINUTES_LETTER: '분',
      SECONDS_LETTER: '초',
    },
    [LANGUAGE_CODE.RUSSIAN]: {
      ENDS_IN_TEXT: 'Осталось',
      DAYS_LETTER: 'д',
      HOURS_LETTER: 'ч',
      MINUTES_LETTER: 'м',
      SECONDS_LETTER: 'c',
    },
    [LANGUAGE_CODE.CHINESE]: {
      ENDS_IN_TEXT: '还剩',
      DAYS_LETTER: '天',
      HOURS_LETTER: '小时',
      MINUTES_LETTER: '分',
      SECONDS_LETTER: '秒',
    },
    [LANGUAGE_CODE.TRADITIONAL_CHINESE]: {
      ENDS_IN_TEXT: '還剩',
      DAYS_LETTER: '天',
      HOURS_LETTER: '小時',
      MINUTES_LETTER: '分',
      SECONDS_LETTER: '秒',
    },
    [LANGUAGE_CODE.ARABIC]: {
      ENDS_IN_TEXT: 'ينتهي في',
      DAYS_LETTER: 'ي',
      HOURS_LETTER: 'س',
      MINUTES_LETTER: 'د',
      SECONDS_LETTER: 'ث',
    },
    [LANGUAGE_CODE.FRENCH]: {
      ENDS_IN_TEXT: 'Plus que',
      DAYS_LETTER: 'j',
      HOURS_LETTER: 'h',
      MINUTES_LETTER: 'min',
      SECONDS_LETTER: 's',
    },
    [LANGUAGE_CODE.GERMAN]: {
      ENDS_IN_TEXT: 'Noch',
      DAYS_LETTER: 'Tage',
      HOURS_LETTER: 'Std.',
      MINUTES_LETTER: 'Min.',
      SECONDS_LETTER: 'Sek.',
    },
    [LANGUAGE_CODE.PORTUGUESE]: {
      ENDS_IN_TEXT: 'Termina em',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'h',
      MINUTES_LETTER: 'm',
      SECONDS_LETTER: 's',
    },
    [LANGUAGE_CODE.HEBREW]: {
      ENDS_IN_TEXT: `מסתיים בעוד`,
      DAYS_LETTER: `י'`,
      HOURS_LETTER: `ש'`,
      MINUTES_LETTER: `ד'`,
      SECONDS_LETTER: `שנ'`,
    },
    [LANGUAGE_CODE.INDONESIAN]: {
      ENDS_IN_TEXT: 'Berakhir',
      DAYS_LETTER: 'H',
      HOURS_LETTER: 'J',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'D',
    },
    [LANGUAGE_CODE.THAI]: {
      ENDS_IN_TEXT: 'สิ้นสุดใน',
      DAYS_LETTER: 'วัน',
      HOURS_LETTER: `ชม.`,
      MINUTES_LETTER: `น.`,
      SECONDS_LETTER: `ว.`,
    },
    [LANGUAGE_CODE.UKRAINIAN]: {
      ENDS_IN_TEXT: 'Завершується',
      DAYS_LETTER: 'дн',
      HOURS_LETTER: 'год',
      MINUTES_LETTER: 'хв',
      SECONDS_LETTER: 'сек',
    },
    [LANGUAGE_CODE.ITALIAN]: {
      ENDS_IN_TEXT: 'Termina tra',
      DAYS_LETTER: 'G',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.BULGARIAN]: {
      ENDS_IN_TEXT: 'Остават',
      DAYS_LETTER: 'д',
      HOURS_LETTER: 'ч',
      MINUTES_LETTER: 'м',
      SECONDS_LETTER: 'с',
    },
    [LANGUAGE_CODE.POLISH]: {
      ENDS_IN_TEXT: 'Koniec za',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.TURKISH]: {
      ENDS_IN_TEXT: 'Bitiş',
      DAYS_LETTER: 'G',
      HOURS_LETTER: 'S',
      MINUTES_LETTER: 'D',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.ROMANIAN]: {
      ENDS_IN_TEXT: 'Expiră în',
      DAYS_LETTER: 'Z',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.CZECH]: {
      ENDS_IN_TEXT: 'Končí za',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.HUNGARIAN]: {
      ENDS_IN_TEXT: 'Akció vége',
      DAYS_LETTER: 'N',
      HOURS_LETTER: 'Ó',
      MINUTES_LETTER: 'P',
      SECONDS_LETTER: 'MP',
    },
    [LANGUAGE_CODE.DUTCH]: {
      ENDS_IN_TEXT: 'Eindigt over',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'U',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.DANISH]: {
      ENDS_IN_TEXT: 'Slutter om',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'T',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.GREEK]: {
      ENDS_IN_TEXT: 'Λήγει σε',
      DAYS_LETTER: 'Η',
      HOURS_LETTER: 'ω',
      MINUTES_LETTER: 'λ',
      SECONDS_LETTER: 'δ',
    },
    [LANGUAGE_CODE.MALAY]: {
      ENDS_IN_TEXT: 'Tamat dalam',
      DAYS_LETTER: 'H',
      HOURS_LETTER: 'J',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.ESTONIAN]: {
      ENDS_IN_TEXT: 'Lõpuni jäänud',
      DAYS_LETTER: 'P',
      HOURS_LETTER: 'p',
      MINUTES_LETTER: 'H',
      SECONDS_LETTER: 's',
    },
    [LANGUAGE_CODE.FINNISH]: {
      ENDS_IN_TEXT: 'Päättyy',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.CROATIAN]: {
      ENDS_IN_TEXT: 'Završava za',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.LITHUANIAN]: {
      ENDS_IN_TEXT: 'Baigiasi po',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.LATVIAN]: {
      ENDS_IN_TEXT: 'Beidzas pēc',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.ENGLISH_UK]: {
      ENDS_IN_TEXT: 'Ends in',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'h',
      MINUTES_LETTER: 'm',
      SECONDS_LETTER: 's',
    },
    [LANGUAGE_CODE.NORWEGIAN]: {
      ENDS_IN_TEXT: 'Slutter om',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 't',
      MINUTES_LETTER: 'min',
      SECONDS_LETTER: 's',
    },
    [LANGUAGE_CODE.SLOVAK]: {
      ENDS_IN_TEXT: 'Končí o',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'H',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
    [LANGUAGE_CODE.SLOVENIAN]: {
      ENDS_IN_TEXT: 'Konča se čez',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'h',
      MINUTES_LETTER: 'min',
      SECONDS_LETTER: 's',
    },
    [LANGUAGE_CODE.SWEDISH]: {
      ENDS_IN_TEXT: 'Slutar om',
      DAYS_LETTER: 'D',
      HOURS_LETTER: 'T',
      MINUTES_LETTER: 'M',
      SECONDS_LETTER: 'S',
    },
  };

  private timer: number;

  connectedCallback() {
    this.elRoot.innerHTML = '';
    this.timer = window.setInterval(
      () => {
        this.currentTime = new Date();
      },
      this.dataIsShowSeconds ? 1000 : 60000,
    );
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  private getDigitStyle = () => {
    const result = {};
    switch (this.dataTheme) {
      case 'color-bg':
        result['backgroundColor'] = this.dataPrimaryColor;
        break;
      case 'gradient-bg':
        result['background'] = `linear-gradient(0deg, ${this.dataPrimaryColor} 0%, ${toColorRgb(this.dataPrimaryColor)
          .replace(')', ',0.6)')
          .replace('rgb', 'rgba')} 73%)`;
        break;
      case 'capsule':
        break;
      default:
        result['color'] = this.dataPrimaryColor;
    }
    return result;
  };

  private getDigitAndLetterStyle = () => {
    const result = {};
    if (this.dataIsDigitAndLetterRound && !this.getIsShowDays()) {
      return this.getDigitStyle();
    }
    return result;
  };

  private getIsShowDays = () => {
    const timeRemaining = this.getTimeRemaining();
    return timeRemaining.days > 0 && !this.dataIsHideDays;
  };

  private getTimerStyle = () => {
    const result = {};
    if (this.dataTheme === 'capsule') {
      result['backgroundColor'] = this.dataPrimaryColor;
    }
    return result;
  };

  private getTimeRemaining = () => {
    let timeRemaining =
      (/^\d{13}$/.test(this.dataEndTimeUtc) ? Number(this.dataEndTimeUtc) : Date.parse(this.dataEndTimeUtc)) -
      Date.parse(this.currentTime.toString());
    if (isNaN(timeRemaining) || timeRemaining < 0) {
      window.clearInterval(this.timer);
      timeRemaining = 0;
    }
    const seconds = Math.floor((timeRemaining / 1000) % 60);
    const minutes = Math.floor((timeRemaining / 1000 / 60) % 60);
    const hours = Math.floor((timeRemaining / (1000 * 60 * 60)) % 24);
    const totalHours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
    return {
      total: timeRemaining,
      days,
      hours,
      totalHours,
      minutes,
      seconds,
    };
  };

  private getColon = (showColon: boolean) => {
    return (
      <span class="cms-colon" style={getStyleObjectFromString(this.dataColonStyle)}>
        {showColon ? ':' : ''}
      </span>
    );
  };

  private getEndsIn = translationInfo => {
    return `${translationInfo.ENDS_IN_TEXT}:`;
  };

  render() {
    const translationInfo = this.TRANSLATION_CONFIG[this.dataLanguageCode]
      ? this.TRANSLATION_CONFIG[this.dataLanguageCode]
      : this.TRANSLATION_CONFIG[LANGUAGE_CODE.ENGLISH];
    const englishInfo = this.TRANSLATION_CONFIG[LANGUAGE_CODE.ENGLISH];
    const timeRemaining = this.getTimeRemaining();
    const isShowDays: boolean = this.getIsShowDays();
    const digitStyle = this.dataIsDigitAndLetterRound && !isShowDays ? {} : this.getDigitStyle();

    return (this.dataEndTimeUtc && timeRemaining.total > 0) || this.dataBehaviorAfterTheEnd === 'show' ? (
      <Host
        class={`cms-${this.dataSize} cms-${this.dataTheme} ${
          this.dataIsEndsInWrap ? 'cms-ends-wrap' : 'cms-ends-nowrap'
        } ${this.dataIsDigitRound && 'cms-digit-round'}
          ${this.dataIsLowercaseLetter && !isShowDays && 'cms-lowercase-letter'}
          ${this.dataIsDigitAndLetterRound && !isShowDays && 'cms-digit-and-letter-round'}
          ${this.dataIsDigitAndLetterRound && isShowDays && 'cms-digit-round'}
        `}
      >
        {this.dataIsShowEndsIn && (
          <div class="cms-ends-in-text" style={getStyleObjectFromString(this.dataEndsInStyle)}>
            {this.getEndsIn(translationInfo)}
          </div>
        )}
        <div class="cms-timer" style={this.getTimerStyle()}>
          {isShowDays && (
            <Fragment>
              <div class={`cms-days`} style={this.getDigitAndLetterStyle()}>
                <span class="cms-digit" style={{ ...digitStyle, ...getStyleObjectFromString(this.dataDigitStyle) }}>
                  {prefixZero(timeRemaining.days, 2)}
                </span>
                <span class="cms-days-letter cms-letter" style={getStyleObjectFromString(this.dataLetterStyle)}>
                  {translationInfo.DAYS_LETTER}
                </span>
              </div>
              {this.dataIsShowColon && this.dataTheme === 'capsule' && this.getColon(false)}
            </Fragment>
          )}
          <div
            class={`cms-hours ${this.dataIsDigitAndLetterRound && !isShowDays && 'cms-digit-and-letter'}`}
            style={this.getDigitAndLetterStyle()}
          >
            <span class="cms-digit" style={{ ...digitStyle, ...getStyleObjectFromString(this.dataDigitStyle) }}>
              {prefixZero(this.dataIsHideDays ? timeRemaining.totalHours : timeRemaining.hours, 2)}
            </span>
            {(!this.dataIsHideLetter || (timeRemaining.days > 0 && !this.dataIsHideDays)) && (
              <span class="cms-hours-letter cms-letter" style={getStyleObjectFromString(this.dataLetterStyle)}>
                {this.dataIsTranslateLetter || isShowDays ? translationInfo.HOURS_LETTER : englishInfo.HOURS_LETTER}
              </span>
            )}
          </div>
          {this.dataIsShowColon && !isShowDays && this.getColon(true)}
          {!isShowDays && (
            <Fragment>
              <div
                class={`cms-minutes ${this.dataIsDigitAndLetterRound && 'cms-digit-and-letter'}`}
                style={this.getDigitAndLetterStyle()}
              >
                <span class="cms-digit" style={{ ...digitStyle, ...getStyleObjectFromString(this.dataDigitStyle) }}>
                  {prefixZero(timeRemaining.minutes, 2)}
                </span>
                {!this.dataIsHideLetter && (
                  <span class="cms-minutes-letter cms-letter" style={getStyleObjectFromString(this.dataLetterStyle)}>
                    {this.dataIsTranslateLetter ? translationInfo.MINUTES_LETTER : englishInfo.MINUTES_LETTER}
                  </span>
                )}
              </div>
              {this.dataIsShowColon && this.dataIsShowSeconds && this.getColon(true)}
              {this.dataIsShowSeconds && (
                <div
                  class={`cms-seconds ${this.dataIsDigitAndLetterRound && 'cms-digit-and-letter'}`}
                  style={this.getDigitAndLetterStyle()}
                >
                  <span class="cms-digit" style={{ ...digitStyle, ...getStyleObjectFromString(this.dataDigitStyle) }}>
                    {prefixZero(timeRemaining.seconds, 2)}
                  </span>
                  {!this.dataIsHideLetter && (
                    <span class="cms-seconds-letter cms-letter" style={getStyleObjectFromString(this.dataLetterStyle)}>
                      {this.dataIsTranslateLetter ? translationInfo.SECONDS_LETTER : englishInfo.SECONDS_LETTER}
                    </span>
                  )}
                </div>
              )}
            </Fragment>
          )}
        </div>
      </Host>
    ) : null;
  }
}
